import React, { useState } from "react";
import * as R from "ramda";
import { Modal, Form, Input, Icon, Avatar, Tooltip } from "antd";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import countries from "i18n-iso-countries";
import { Link } from "react-router-dom";

import connection from "../../../assets/post.svg";
import connected from "../../../assets/connected.svg";
import {
  connectCompanySocialAccount,
  CONNECT_COMPANY_SOCIAL_ACCOUNT
} from "../../../store/actions/company";
import { isLoading } from "../../../utils/actions";
import Button from "../../../components/Button";
import { httpToHttps } from "../../../utils/images";

import styled from "styled-components/macro";

const Title = styled.div`
  margin: 0px;
  font-size: 22px;
  margin-top: 10px;
  font-weight: bold;
  color: ${({ theme }) => theme["@gray-80"]};
  text-align: center;
`;

const Subtitle = styled.p`
  margin: 0px;
  font-size: 16px;
  color: ${({ theme }) => theme["@gray-80"]};
  text-align: center;
`;

const AvatarContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  margin-top: 40px;
  margin-bottom: 40px;
`;
const CustomAvatar = styled(Avatar)`
  border: 1px solid #e5e9ed;
`;

const ConnectionImg = styled.img`
  width: 64px;
  margin-left: 5px;
  margin-right: 5px;
`;

const Label = styled.span`
  font-size: 14px;

  .text {
    color: ${({ theme }) => theme["@gray-60"]};
  }

  .icon {
    margin-left: 1ch;
    color: #3d64ff;
    cursor: pointer;
  }
`;

const TooltipContent = styled.div`
  display: flex;
  justify-content: center;
  gap: 15px;
  margin: 5px;

  .icon {
    font-size: 20px;
    margin-top: 10px;
  }
`;

const WrapperButton = styled.div`
  display: flex;
  justify-content: center;
  gap: 12px;
  margin-top: 40px;
  margin-bottom: 20px;
`;

const MultipostingModal = ({ form, visible = true, onClose, type }) => {
  const { t } = useTranslation();
  const { getFieldDecorator } = form;

  const dispatch = useDispatch();
  const loading = useSelector(state =>
    isLoading(CONNECT_COMPANY_SOCIAL_ACCOUNT, state)
  );

  const company = useSelector(({ company }) => company);
  const provider = `${type.name} ${
    type.country === "INT" ? "" : countries.getName(type.country, "es")
  }`.trim();
  const [step, setStep] = useState("init");

  const InfoAvatars = () => (
    <AvatarContainer>
      <CustomAvatar size={76} src={httpToHttps(company.logo)} />
      <ConnectionImg
        src={step === "init" ? connection : connected}
        alt="Link"
      />
      <CustomAvatar size={76} src={type.logo} />
    </AvatarContainer>
  );

  return (
    <Modal
      width={570}
      visible={visible}
      destroyOnClose={true}
      onCancel={() => !loading && onClose()}
      maskClosable={false}
      closable={!loading}
      keyboard={false}
      footer={null}
    >
      {step === "init" ? (
        <div>
          <Title>
            <div>{`${t(
              "opportunity:wizard.multiposting.integrations.connect"
            )} ${provider}`}</div>
            <div>
              {t("opportunity:wizard.multiposting.integrations.posting")}
            </div>
          </Title>
          <InfoAvatars />
          <div style={{ marginBottom: 32 }}>
            <Subtitle>
              {t("opportunity:wizard.multiposting.integrations.companyInfo")}
            </Subtitle>
            <Subtitle>
              <strong>{provider}</strong>
            </Subtitle>
          </div>

          <Form
            layout="horizontal"
            style={{ width: 370, margin: "auto" }}
            onSubmit={ev => {
              ev.preventDefault();
              form.validateFields(async (err, values) => {
                if (!err) {
                  await dispatch(
                    connectCompanySocialAccount({
                      provider: type.provider,
                      country: type.country,
                      ...values
                    })
                  );
                  setStep("connected");
                }
              });
            }}
          >
            {type.config.map(field => (
              <Form.Item
                key={field.name}
                name={field.name}
                label={
                  <Label>
                    <span className="text">
                      {t(
                        `opportunity:wizard.multiposting.integrations.${type.provider}.${field.name}`
                      )}
                    </span>
                    {field.tooltip && (
                      <Tooltip
                        title={
                          <TooltipContent>
                            <div>
                              <Icon className="icon" type="info-circle" />
                            </div>
                            <div>{field.tooltip(t)}</div>
                          </TooltipContent>
                        }
                      >
                        <Icon className="icon" type="question-circle" />
                      </Tooltip>
                    )}
                  </Label>
                }
              >
                {getFieldDecorator(field.name, {
                  rules: field.validation.map(
                    R.evolve({ message: f => <div>{f(t)}</div> })
                  )
                })(<Input />)}
              </Form.Item>
            ))}

            <WrapperButton>
              <Button
                type="quaternary"
                disabled={loading}
                onClick={onClose}
                size="lg"
              >
                {t("global:cancel")}
              </Button>
              <Button
                data-test="discard-button"
                loading={loading}
                htmlType="submit"
                type="primary"
                size="lg"
              >
                {t("global:connect")}
              </Button>
            </WrapperButton>
          </Form>
        </div>
      ) : (
        <div>
          <Title>
            {t("opportunity:wizard.multiposting.integrations.success")}
          </Title>
          <InfoAvatars />
          <div style={{ marginBottom: 32 }}>
            <Subtitle>
              {`${t(
                "opportunity:wizard.multiposting.integrations.successTitle"
              )} ${provider}`}
            </Subtitle>
          </div>
          <WrapperButton>
            <Button
              type="quaternary"
              disabled={loading}
              onClick={onClose}
              size="lg"
            >
              {t("global:close")}
            </Button>
            <Link to="/oportunidades">
              <Button data-test="discard-button" type="primary" size="lg">
                {t("global:viewOpps")}
              </Button>
            </Link>
          </WrapperButton>
        </div>
      )}
    </Modal>
  );
};

export default Form.create()(MultipostingModal);
